import { useState, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { useApi } from "./components.js";
import { Button, buttonVariants } from "@/components/ui/button";
import { SpinnerGap } from "@phosphor-icons/react";
import type { VariantProps } from "class-variance-authority";
import type { ActionResult } from "scantling-common";

interface ActionButtonProps
  extends
    Omit<React.ComponentProps<"button">, "onClick" | "onError">,
    VariantProps<typeof buttonVariants> {
  verb: string;
  params?: Record<string, unknown>;
  onActionSuccess?: (result: ActionResult) => void;
  onActionError?: (error: Error) => void;
}

export function ActionButton({
  verb,
  params = {},
  onActionSuccess,
  onActionError,
  children,
  disabled,
  ...props
}: ActionButtonProps) {
  const { getToken } = useAuth();
  const pool = useApi();
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    setLoading(true);
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (token === null) {
        throw new Error("Not authenticated");
      }
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) {
        throw new Error("No org available");
      }
      const result = await orgApi.executeAction({ verb, params });
      if (result.success) {
        onActionSuccess?.(result);
      } else {
        throw new Error(result.error ?? "Action failed");
      }
    } catch (err) {
      onActionError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
      pool.release();
    }
  }, [verb, params, getToken, pool, onActionSuccess, onActionError]);

  return (
    <Button onClick={handleClick} disabled={disabled || loading} {...props}>
      {loading ? <SpinnerGap className="animate-spin" /> : null}
      {children}
    </Button>
  );
}
